<script lang="ts" setup>
// Generated with AI

import { ref } from "vue"
import { route } from "@/common/utils/ziggy"
import axios from "axios"

const props = defineProps<{
  patterns: string[]
  emailConfirmationEnabled: boolean
}>()

const patternsText = ref(props.patterns.join("\n"))
const saving = ref(false)
const saved = ref(false)
const error = ref("")
const featureEnabled = ref(props.emailConfirmationEnabled)
const toggling = ref(false)

async function toggleFeature() {
  toggling.value = true
  try {
    const newValue = !featureEnabled.value
    await axios.post(route("admin.settings.set"), {
      key: "features.email_confirmation",
      value: newValue,
    })
    featureEnabled.value = newValue
  } catch (e) {
    console.error("Failed to toggle feature:", e)
  } finally {
    toggling.value = false
  }
}

async function save() {
  saving.value = true
  saved.value = false
  error.value = ""

  try {
    await axios.put(route("admin.registration-approvals.update-patterns"), {
      patterns: patternsText.value,
    })
    saved.value = true
  } catch (e: any) {
    if (e.response?.status === 422) {
      const patternsError = e.response.data.errors?.patterns
      error.value = Array.isArray(patternsError) ? patternsError[0] : (patternsError || "Invalid patterns.")
    } else {
      error.value = "Failed to save patterns."
    }
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="container-fluid">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h1>Approval Patterns</h1>
      <a :href="route('admin.registration-approvals.index')" class="btn btn-outline-secondary">
        Back to Approvals
      </a>
    </div>

    <div class="alert alert-info">
      <p>One regular expression per line. Applied to the normalized email address (dots and "plussed" suffixes stripped).</p>

      <p>Example: <code>(?i)@gmail\.com$</code></p>

      <p>To require manual approval of <i>all</i> registrations, add a line at the top consisting of a single dot</p>
    </div>

    <div class="card mb-4">
      <div class="card-body d-flex align-items-center">
        <b-form-checkbox
          switch
          :checked="featureEnabled"
          :disabled="toggling"
          @change="toggleFeature"
        >
          Email confirmation <strong>{{ featureEnabled ? 'enabled' : 'disabled' }}</strong>
        </b-form-checkbox>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <div class="mb-3">
      <textarea
        v-model="patternsText"
        class="form-control"
        rows="10"
        placeholder="(?i)@gmail\.com$"
      ></textarea>
    </div>

    <button class="btn btn-primary" :disabled="saving" @click="save">
      {{ saving ? "Saving..." : "Save Patterns" }}
    </button>

    <span v-if="saved" class="text-success ms-2">&#10003; Saved</span>
  </div>
</template>
